import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Database, Code, Table, BarChart, Network } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Badge, FloatingDock } from '@unwritten/ui-primitives';
import { useSidebar } from '@unwritten/ui-patterns';
import { PRIMITIVE_METADATA, PrimitiveGraphTab } from '@unwritten/ui-domains';
import type { PrimitiveType } from '../../types/primitives';

type Tab = 'overview' | 'schema' | 'operations' | 'data' | 'stats' | 'graph';

export function ConfigDetail() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const { mode } = useSidebar();
  
  const primitive = id ? PRIMITIVE_METADATA[id as PrimitiveType] : null;
  
  if (!primitive) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-uw-text-primary mb-2">Primitive Not Found</h2>
          <p className="text-uw-text-secondary mb-4">The primitive &quot;{id}&quot; does not exist.</p>
          <Link to="/configs" className="text-uw-interactive-primary hover:text-uw-interactive-primary-hover">
            Back to Primitives
          </Link>
        </div>
      </div>
    );
  }

  const Icon = primitive.icon;

  const tabs: { id: Tab; label: string; icon: typeof BookOpen }[] = [
    { id: 'overview', label: 'Overview', icon: BookOpen },
    { id: 'schema', label: 'Schema', icon: Database },
    { id: 'operations', label: 'Operations', icon: Code },
    { id: 'graph', label: 'Relationships', icon: Network },
    { id: 'data', label: 'Data Browser', icon: Table },
    { id: 'stats', label: 'Stats', icon: BarChart },
  ];

  const dockItems = tabs.map((tab) => {
    const TabIcon = tab.icon;
    return {
      title: tab.label,
      icon: <TabIcon className="h-full w-full text-neutral-500 dark:text-neutral-300" />,
      onClick: () => setActiveTab(tab.id),
    };
  });

  const activeTabIndex = tabs.findIndex((tab) => tab.id === activeTab);

  // Sync tab with URL hash
  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash && tabs.some(tab => tab.id === hash)) {
      setActiveTab(hash as Tab);
    }
  }, []);

  // Update hash when tab changes
  useEffect(() => {
    if (mode === 'sidebar') {
      // Only manage hash in sidebar mode
      window.location.hash = activeTab;
    }
  }, [activeTab, mode]);

  return (
    <div className="container mx-auto px-4 py-8">
      <Link
        to="/configs"
        className="inline-flex items-center text-uw-interactive-primary hover:text-uw-interactive-primary-hover mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Primitives
      </Link>

      <div 
        className="border-t-4 mb-6" 
        style={{ borderTopColor: `var(--uw-primitive-${primitive.id})` }}
      />

      <div className="flex items-center gap-4 mb-8">
        <div
          className="p-4 rounded-lg"
          style={{ backgroundColor: `var(--uw-primitive-${primitive.id}-surface)` }}
        >
          <Icon
            className="w-8 h-8"
            style={{ color: `var(--uw-primitive-${primitive.id})` }}
          />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-uw-text-primary">{primitive.name}</h1>
          <p className="text-uw-text-secondary mt-1">{primitive.description}</p>
        </div>
      </div>

      {/* Only show local FloatingDock in sidebar mode */}
      {mode === 'sidebar' && (
        <FloatingDock
          items={dockItems}
          activeIndex={activeTabIndex}
          accentColor={`var(--uw-primitive-${primitive.id})`}
        />
      )}

      <div className="bg-uw-surface-default rounded-lg shadow-card border border-uw-border-default p-8 mb-24">
        {activeTab === 'overview' && <OverviewTab primitive={primitive} />}
        {activeTab === 'schema' && <SchemaTab primitive={primitive} />}
        {activeTab === 'operations' && <OperationsTab primitive={primitive} />}
        {activeTab === 'graph' && <PrimitiveGraphTab primitiveType={primitive.id} />}
        {activeTab === 'data' && <DataBrowserTab primitive={primitive} />}
        {activeTab === 'stats' && <StatsTab primitive={primitive} />}
      </div>
    </div>
  );
}

function OverviewTab({ primitive }: { primitive: typeof PRIMITIVE_METADATA[PrimitiveType] }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2 text-uw-text-primary">Description</h3>
        <p className="text-uw-text-secondary">{primitive.description}</p>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2 text-uw-text-primary">Key Features</h3>
        <div className="flex flex-wrap gap-2">
          {primitive.tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 text-sm bg-uw-surface-elevated text-uw-text-secondary border border-uw-border-default rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2 text-uw-text-primary">Service Path</h3>
        <code className="block px-4 py-2 bg-uw-surface-elevated rounded text-sm text-uw-text-primary">
          {primitive.servicePath}
        </code>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2 text-uw-text-primary">Database</h3>
        <p className="text-uw-text-secondary">
          {primitive.hasDatabase ? (
            <span className="text-uw-success font-medium">✓ Persistent storage enabled</span>
          ) : (
            <span className="text-uw-text-tertiary">Runtime only (ephemeral, no database)</span>
          )}
        </p>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2 text-uw-text-primary">Category</h3>
        <Badge variant={`category-${primitive.category}` as any} size="md">
          {primitive.category}
        </Badge>
      </div>
    </div>
  );
}

function SchemaTab({ primitive }: { primitive: typeof PRIMITIVE_METADATA[PrimitiveType] }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2 text-uw-text-primary">TypeScript Types</h3>
        <p className="text-uw-text-secondary mb-2">
          Types are imported from <code className="bg-uw-surface-elevated px-2 py-1 rounded text-sm text-uw-text-primary">@unwritten/shared-types</code>
        </p>
        <div className="bg-uw-surface-elevated border border-uw-border-default rounded p-4 text-sm text-uw-text-secondary">
          View source at: <code>packages/game-primitives/src/{primitive.id}/types.ts</code>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2 text-uw-text-primary">Zod Validation Schemas</h3>
        <p className="text-uw-text-secondary mb-2">
          Validation schemas for runtime type checking
        </p>
        <div className="bg-uw-surface-elevated border border-uw-border-default rounded p-4 text-sm text-uw-text-secondary">
          Exported from <code>@unwritten/shared-types</code>
        </div>
      </div>

      {primitive.hasDatabase && (
        <div>
          <h3 className="text-lg font-semibold mb-2 text-uw-text-primary">SurrealDB Schema</h3>
          <p className="text-uw-text-secondary mb-2">
            Database schema definitions, fields, and indexes
          </p>
          <div className="bg-uw-surface-elevated border border-uw-border-default rounded p-4 text-sm text-uw-text-secondary">
            View source at: <code>packages/game-primitives/src/{primitive.id}/db-schema.ts</code>
          </div>
        </div>
      )}
    </div>
  );
}

function OperationsTab({ primitive }: { primitive: typeof PRIMITIVE_METADATA[PrimitiveType] }) {
  const commonOps = [
    { name: 'create', signature: `create(data: Omit<T, 'id'>): Promise<T>`, description: 'Create a new record' },
    { name: 'getById', signature: `getById(id: string): Promise<T | null>`, description: 'Get record by ID' },
    { name: 'update', signature: `update(id: string, data: Partial<T>): Promise<T>`, description: 'Update existing record' },
    { name: 'delete', signature: `delete(id: string): Promise<void>`, description: 'Delete record' },
    { name: 'listAll', signature: `listAll(): Promise<T[]>`, description: 'List all records' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4 text-uw-text-primary">Service Methods</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-uw-border-default">
            <thead className="bg-uw-surface-elevated">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-uw-text-tertiary uppercase">Method</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-uw-text-tertiary uppercase">Signature</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-uw-text-tertiary uppercase">Description</th>
              </tr>
            </thead>
            <tbody className="bg-uw-surface-default divide-y divide-uw-border-default">
              {commonOps.map((op) => (
                <tr key={op.name}>
                  <td className="px-4 py-3 text-sm font-medium text-uw-text-primary">{op.name}</td>
                  <td className="px-4 py-3 text-sm text-uw-text-secondary">
                    <code className="text-xs">{op.signature}</code>
                  </td>
                  <td className="px-4 py-3 text-sm text-uw-text-secondary">{op.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-uw-info-surface border border-uw-info/20 rounded p-4">
        <p className="text-sm text-uw-info">
          View complete service implementation at: <code>packages/game-primitives/src/{primitive.id}/service.ts</code>
        </p>
      </div>
    </div>
  );
}

function DataBrowserTab({ primitive }: { primitive: typeof PRIMITIVE_METADATA[PrimitiveType] }) {
  if (!primitive.hasDatabase) {
    return (
      <div className="text-center py-12 text-uw-text-tertiary">
        <Table className="w-12 h-12 mx-auto mb-4 text-uw-text-disabled" />
        <p>This primitive has no database storage (runtime only)</p>
      </div>
    );
  }

  return (
    <div className="text-center py-12 text-uw-text-tertiary">
      <Table className="w-12 h-12 mx-auto mb-4 text-uw-text-disabled" />
      <p className="mb-2">Data browser coming soon</p>
      <p className="text-sm">Connect to SurrealDB to browse and inspect records</p>
    </div>
  );
}

function StatsTab({ primitive }: { primitive: typeof PRIMITIVE_METADATA[PrimitiveType] }) {
  if (!primitive.hasDatabase) {
    return (
      <div className="text-center py-12 text-uw-text-tertiary">
        <BarChart className="w-12 h-12 mx-auto mb-4 text-uw-text-disabled" />
        <p>No statistics available (runtime only primitive)</p>
      </div>
    );
  }

  return (
    <div className="text-center py-12 text-uw-text-tertiary">
      <BarChart className="w-12 h-12 mx-auto mb-4 text-uw-text-disabled" />
      <p className="mb-2">Statistics coming soon</p>
      <p className="text-sm">Record counts, distributions, and analytics</p>
    </div>
  );
}
